export interface EffectParams {
  speed: number
  intensity: number
  color: string
  bgColor: string
  playing: boolean
}

export interface Effect {
  id: string
  name: string
  usesColor?: boolean
  render: (
    ctx: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    width: number,
    height: number,
    timeMs: number,
    params: EffectParams,
  ) => void
}

/* ------------------------------------------------------------------ */
/* Shared helpers                                                     */
/* ------------------------------------------------------------------ */

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 99, g: 102, b: 241 }
}

// A reusable offscreen canvas so per-pixel effects don't allocate every frame.
let scratch: HTMLCanvasElement | null = null
let scratchCtx: CanvasRenderingContext2D | null = null
function getSourceData(source: HTMLCanvasElement, w: number, h: number) {
  if (!scratch) {
    scratch = document.createElement('canvas')
    scratchCtx = scratch.getContext('2d', { willReadFrequently: true })
  }
  if (scratch!.width !== w || scratch!.height !== h) {
    scratch!.width = w
    scratch!.height = h
  }
  scratchCtx!.clearRect(0, 0, w, h)
  scratchCtx!.drawImage(source, 0, 0, w, h)
  return scratchCtx!.getImageData(0, 0, w, h)
}

const clamp = (v: number, lo: number, hi: number) =>
  v < lo ? lo : v > hi ? hi : v

function lum(d: Uint8ClampedArray, i: number) {
  return (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255
}

// HSL -> RGB (h in [0,360), s/l in [0,1])
function hsl(h: number, s: number, l: number) {
  h = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0,
    g = 0,
    b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255]
}

/* ------------------------------------------------------------------ */
/* Effect registry                                                    */
/* ------------------------------------------------------------------ */

export const effects: Effect[] = [
  {
    id: 'none',
    name: 'Original',
    render: (ctx, source, w, h) => {
      ctx.drawImage(source, 0, 0, w, h)
    },
  },

  /* ---- Liquid Metal / Chrome ------------------------------------- */
  {
    id: 'liquid-metal',
    name: 'Liquid Metal',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const intensity = params.intensity / 100
      const src = getSourceData(source, w, h)
      const sd = src.data
      const out = ctx.createImageData(w, h)
      const od = out.data
      // Faint metallic tint from the chosen color (default reads as cool
      // chrome). Kept subtle so it stays a "shiny metal", not a wild color.
      const tint = hexToRgb(params.color)
      const tk = 0.22

      // Keep the logo readable: its own luminance sets the base metal tone,
      // then a slow flowing specular highlight sweeps across for the sheen.
      const warp = 0.04 + intensity * 0.12 // how much the highlight drifts
      const bands = 1.6 + intensity * 2.4 // highlight streak frequency (low)
      const gloss = 0.5 + intensity * 0.5 // strength of the specular glints

      const alphaAt = (x: number, y: number) =>
        sd[(clamp(y, 0, h - 1) * w + clamp(x, 0, w - 1)) * 4 + 3]

      for (let y = 0; y < h; y++) {
        const ny = y / h
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4
          const a = sd[i + 3]
          if (a === 0) continue
          const nx = x / w

          // The logo's own light/dark values become the metal's base tone,
          // so the original mark stays clearly visible.
          const L = lum(sd, i)
          const baseTone = 0.2 + L * 0.7 // 0.2 dark metal -> 0.9 bright metal

          // A slow flowing band gives a subtle brushed-metal shimmer.
          const u =
            nx +
            warp * Math.sin(ny * 6 + time * 1.1) +
            warp * 0.5 * Math.sin((nx + ny) * 5 - time)
          const v = ny + warp * Math.cos(nx * 6 - time * 0.9)
          const env =
            0.5 +
            0.5 * Math.sin((u * bands + v * bands * 0.7) * Math.PI + time * 1.6)

          // Base grey metal: logo tone, gently modulated by the reflection.
          const gray = baseTone * (0.85 + 0.15 * env) * 255

          // Tight moving specular glints, stronger on the brighter areas.
          const spec = Math.pow(env, 8) * gloss * (0.4 + L * 0.6)

          // Rim highlight on the silhouette edge for a rounded metal read.
          const edge =
            (255 * 4 -
              (alphaAt(x - 1, y) +
                alphaAt(x + 1, y) +
                alphaAt(x, y - 1) +
                alphaAt(x, y + 1))) /
            255
          const rim = clamp(edge, 0, 1) * 0.5

          const highlight = spec * 235 + rim * 190

          // Neutral silver with a faint pull toward the chosen tint.
          const r = gray * (1 - tk) + gray * (tint.r / 255) * tk + highlight
          const g = gray * (1 - tk) + gray * (tint.g / 255) * tk + highlight
          const b = gray * (1 - tk) + gray * (tint.b / 255) * tk + highlight

          od[i] = clamp(r, 0, 255)
          od[i + 1] = clamp(g, 0, 255)
          od[i + 2] = clamp(b, 0, 255)
          od[i + 3] = a
        }
      }
      ctx.putImageData(out, 0, 0)
    },
  },

  /* ---- Molten Flow (domain-warp liquid) -------------------------- */
  {
    id: 'molten',
    name: 'Molten Flow',
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const amp = 4 + (params.intensity / 100) * 22
      const src = getSourceData(source, w, h)
      const sd = src.data
      const out = ctx.createImageData(w, h)
      const od = out.data

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          // Layered sine flow field -> molten distortion.
          const wx =
            Math.sin(y * 0.03 + time * 1.3) * amp +
            Math.sin((x + y) * 0.02 - time) * amp * 0.6
          const wy =
            Math.cos(x * 0.035 - time * 1.1) * amp +
            Math.sin((x - y) * 0.02 + time * 0.8) * amp * 0.6
          const sx = clamp(Math.round(x + wx), 0, w - 1)
          const sy = clamp(Math.round(y + wy), 0, h - 1)
          const si = (sy * w + sx) * 4
          const di = (y * w + x) * 4
          od[di] = sd[si]
          od[di + 1] = sd[si + 1]
          od[di + 2] = sd[si + 2]
          od[di + 3] = sd[si + 3]
        }
      }
      ctx.putImageData(out, 0, 0)
    },
  },

  /* ---- Animated ASCII -------------------------------------------- */
  {
    id: 'ascii',
    name: 'ASCII',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const cell = Math.max(6, Math.round(13 - (params.intensity / 100) * 6))
      const ramp = ' .:-=+*#%@'
      const src = getSourceData(source, w, h)
      const sd = src.data
      ctx.clearRect(0, 0, w, h)
      ctx.font = `${Math.round(cell * 1.15)}px "SFMono-Regular", Menlo, monospace`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const tint = hexToRgb(params.color)

      for (let cy = 0; cy < h; cy += cell) {
        for (let cx = 0; cx < w; cx += cell) {
          let sum = 0
          let count = 0
          let alpha = 0
          for (let y = cy; y < cy + cell && y < h; y++) {
            for (let x = cx; x < cx + cell && x < w; x++) {
              const i = (y * w + x) * 4
              alpha += sd[i + 3]
              if (sd[i + 3] > 20) {
                sum += lum(sd, i)
                count++
              }
            }
          }
          const coverage = alpha / (cell * cell * 255)
          if (coverage < 0.08 || count === 0) continue
          const avg = sum / count
          // A single smooth diagonal wave shimmers through the ramp — tidy,
          // no per-cell noise.
          const wave = 0.5 + 0.5 * Math.sin(time * 2 - (cx + cy) * 0.012)
          const level = avg * 0.8 + wave * 0.2
          const idx = clamp(
            Math.round(level * (ramp.length - 1)),
            0,
            ramp.length - 1,
          )
          const ch = ramp[idx]
          if (ch === ' ') continue
          const b = 0.55 + level * 0.45
          ctx.fillStyle = `rgb(${clamp(tint.r * b, 0, 255) | 0},${
            clamp(tint.g * b, 0, 255) | 0
          },${clamp(tint.b * b, 0, 255) | 0})`
          ctx.fillText(ch, cx + cell / 2, cy + cell / 2)
        }
      }
    },
  },

  /* ---- Dithered (Floyd–Steinberg) -------------------------------- */
  {
    id: 'dither',
    name: 'Dithered',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const src = getSourceData(source, w, h)
      const sd = src.data
      // grayscale height field (float), -1 marks transparent
      const g = new Float32Array(w * h)
      for (let i = 0, p = 0; p < w * h; p++, i += 4) {
        g[p] = sd[i + 3] < 20 ? -1 : lum(sd, i)
      }
      // Gentle threshold drift so the stipple shimmers softly instead of
      // flashing the whole logo on and off.
      const thresh = 0.5 + 0.04 * Math.sin(time * 1.2)
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const p = y * w + x
          if (g[p] < 0) continue
          const old = g[p]
          const nv = old < thresh ? 0 : 1
          const err = old - nv
          g[p] = nv
          // diffuse error to neighbours (skip transparent)
          if (x + 1 < w && g[p + 1] >= 0) g[p + 1] += (err * 7) / 16
          if (x > 0 && y + 1 < h && g[p + w - 1] >= 0)
            g[p + w - 1] += (err * 3) / 16
          if (y + 1 < h && g[p + w] >= 0) g[p + w] += (err * 5) / 16
          if (x + 1 < w && y + 1 < h && g[p + w + 1] >= 0)
            g[p + w + 1] += (err * 1) / 16
        }
      }
      const out = ctx.createImageData(w, h)
      const od = out.data
      const c = hexToRgb(params.color)
      for (let p = 0, i = 0; p < w * h; p++, i += 4) {
        if (g[p] < 0) {
          od[i + 3] = 0
        } else if (g[p] >= 0.5) {
          od[i] = c.r
          od[i + 1] = c.g
          od[i + 2] = c.b
          od[i + 3] = 255
        } else {
          od[i + 3] = 0
        }
      }
      ctx.putImageData(out, 0, 0)
    },
  },

  /* ---- Chromatic Glitch ------------------------------------------ */
  {
    id: 'chromatic',
    name: 'Chromatic',
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const maxShift = 2 + (params.intensity / 100) * 16
      const src = getSourceData(source, w, h)
      const sd = src.data
      const out = ctx.createImageData(w, h)
      const od = out.data
      const base = Math.sin(time * 2.2) * maxShift

      for (let y = 0; y < h; y++) {
        // occasional per-row jitter for a torn-signal feel
        const jitter =
          Math.random() > 0.94 ? (Math.random() - 0.5) * maxShift * 2 : 0
        const shift = Math.round(base + jitter)
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4
          const rx = clamp(x + shift, 0, w - 1)
          const bx = clamp(x - shift, 0, w - 1)
          const ri = (y * w + rx) * 4
          const bi = (y * w + bx) * 4
          od[i] = sd[ri]
          od[i + 1] = sd[i + 1]
          od[i + 2] = sd[bi + 2]
          od[i + 3] = Math.max(sd[i + 3], sd[ri + 3], sd[bi + 3])
        }
      }
      ctx.putImageData(out, 0, 0)
      // scanline overlay clipped to the logo
      ctx.save()
      ctx.globalCompositeOperation = 'source-atop'
      ctx.globalAlpha = 0.18
      ctx.fillStyle = '#000'
      for (let y = (time * 60) % 3; y < h; y += 3) ctx.fillRect(0, y, w, 1)
      ctx.restore()
    },
  },

  /* ---- Hologram / Iridescent foil -------------------------------- */
  {
    id: 'hologram',
    name: 'Hologram',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const spread = 0.4 + (params.intensity / 100) * 1.6
      const src = getSourceData(source, w, h)
      const sd = src.data
      const out = ctx.createImageData(w, h)
      const od = out.data
      // A soft highlight band that sweeps down the logo.
      const sweep = (time * 0.25) % 1

      for (let y = 0; y < h; y++) {
        const ny = y / h
        // fine scanlines + the travelling sweep highlight
        const scan = 0.82 + 0.18 * Math.sin(y * 0.5 + time * 4)
        const dist = Math.abs(ny - sweep)
        const glow = Math.max(0, 1 - dist * 6) * 0.5
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4
          if (sd[i + 3] === 0) continue
          const l = lum(sd, i)
          // Slow, smooth iridescent drift keyed to the logo's own tone.
          const hue = (x * spread + l * 120 + time * 45) % 360
          const [r, g, b] = hsl(hue, 0.85, 0.42 + l * 0.32)
          od[i] = clamp(r * scan + glow * 255, 0, 255)
          od[i + 1] = clamp(g * scan + glow * 255, 0, 255)
          od[i + 2] = clamp(b * scan + glow * 255, 0, 255)
          od[i + 3] = sd[i + 3]
        }
      }
      ctx.putImageData(out, 0, 0)
    },
  },

  /* ---- Plasma Energy --------------------------------------------- */
  {
    id: 'plasma',
    name: 'Plasma',
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const scale = 0.018 + (params.intensity / 100) * 0.04
      const src = getSourceData(source, w, h)
      const sd = src.data
      const out = ctx.createImageData(w, h)
      const od = out.data
      const cx = w / 2
      const cy = h / 2

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4
          if (sd[i + 3] === 0) continue
          const dist = Math.hypot(x - cx, y - cy)
          const v =
            Math.sin(x * scale + time * 0.8) +
            Math.sin(y * scale + time) +
            Math.sin((x + y) * scale * 0.7 + time * 0.6) +
            Math.sin(dist * scale - time * 1.4)
          const hue = (v * 55 + time * 30 + 200) % 360
          const l = lum(sd, i)
          // Keep the logo's form readable: brighter areas stay brighter and a
          // touch less saturated, so the mark reads through the energy field.
          const [r, g, b] = hsl(hue, 0.85 - l * 0.25, 0.32 + l * 0.42)
          od[i] = r
          od[i + 1] = g
          od[i + 2] = b
          od[i + 3] = sd[i + 3]
        }
      }
      ctx.putImageData(out, 0, 0)
    },
  },

  /* ---- Pixel Sort ------------------------------------------------- */
  {
    id: 'pixel-sort',
    name: 'Pixel Sort',
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const src = getSourceData(source, w, h)
      const sd = src.data
      const threshold = 0.35 + 0.25 * (0.5 + 0.5 * Math.sin(time))
      const intensityCut = 1 - params.intensity / 100

      for (let y = 0; y < h; y++) {
        let x = 0
        while (x < w) {
          // find a span of bright, opaque pixels to sort
          let start = x
          while (
            start < w &&
            (sd[(y * w + start) * 4 + 3] === 0 ||
              lum(sd, (y * w + start) * 4) < threshold)
          )
            start++
          let end = start
          while (
            end < w &&
            sd[(y * w + end) * 4 + 3] > 0 &&
            lum(sd, (y * w + end) * 4) >= threshold
          )
            end++
          if (end - start > 2 && Math.random() > intensityCut) {
            const span = []
            for (let k = start; k < end; k++) {
              const i = (y * w + k) * 4
              span.push([sd[i], sd[i + 1], sd[i + 2], sd[i + 3], lum(sd, i)])
            }
            span.sort((a, b) => a[4] - b[4])
            for (let k = start; k < end; k++) {
              const i = (y * w + k) * 4
              const s = span[k - start]
              sd[i] = s[0]
              sd[i + 1] = s[1]
              sd[i + 2] = s[2]
              sd[i + 3] = s[3]
            }
          }
          x = end > start ? end : x + 1
        }
      }
      ctx.putImageData(src, 0, 0)
    },
  },

  /* ---- Neon Glow -------------------------------------------------- */
  {
    id: 'neon-glow',
    name: 'Neon Glow',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const pulse = Math.sin(time * 2.4) * 0.5 + 0.5
      const blur = 6 + (params.intensity / 100) * 40 * (0.55 + pulse * 0.45)
      ctx.save()
      // Layered glow: wide soft halo, then a tighter one, building intensity.
      ctx.shadowColor = params.color
      ctx.shadowBlur = blur
      ctx.drawImage(source, 0, 0, w, h)
      ctx.drawImage(source, 0, 0, w, h)
      ctx.shadowBlur = blur * 0.45
      ctx.drawImage(source, 0, 0, w, h)
      // Crisp core on top so the logo stays sharp inside the glow.
      ctx.shadowBlur = 0
      ctx.drawImage(source, 0, 0, w, h)
      ctx.restore()
    },
  },

  /* ---- Halftone (rotated screen) --------------------------------- */
  {
    id: 'halftone',
    name: 'Halftone',
    usesColor: true,
    render: (ctx, source, w, h, t, params) => {
      const time = (t / 1000) * params.speed
      const intensity = params.intensity / 100
      const src = getSourceData(source, w, h)
      const sd = src.data
      ctx.clearRect(0, 0, w, h)

      // Classic print halftone: dots on a screen rotated ~18° (so the grid
      // never lines up with the artwork). Dot SIZE encodes the logo's darkness
      // there, and the radius is capped so at maximum the dots only just touch
      // — a crisp screened print, never a merged blob.
      // Fine, dense screen: small cells (so lots of dots) that get even finer
      // as Intensity rises. Floor keeps the dot count sane for the render loop.
      const cell = Math.max(3, Math.round(6 - intensity * 3))
      const angle = 0.32
      const cosA = Math.cos(angle)
      const sinA = Math.sin(angle)
      const cx0 = w / 2
      const cy0 = h / 2
      // Dots stay well under the cell spacing so there are clear gaps between
      // them — a fine screen, never a merged blob.
      const maxR = cell * 0.5
      const ink = hexToRgb(params.color)

      // Animated radial ripple: a wave pulses outward from the centre,
      // swelling each dot as its crest passes and lighting up a bright
      // expanding ring — the "sonar pulse" that makes the halftone feel alive.
      const rippleFreq = 0.045
      const rippleSpeed = time * 4
      const maxDist = Math.hypot(w, h) / 2

      // Sample one pixel; returns [ink, luminance] (ink = opacity).
      const sample = (x: number, y: number) => {
        if (x < 0 || y < 0 || x >= w || y >= h) return null
        const i = (y * w + x) * 4
        const a = sd[i + 3]
        if (a < 8) return null
        return [a / 255, lum(sd, i)] as const
      }

      const diag = Math.ceil(Math.hypot(w, h) / cell) + 2
      for (let gy = -diag; gy <= diag; gy++) {
        for (let gx = -diag; gx <= diag; gx++) {
          // Rotated lattice point -> world position.
          const lx = gx * cell
          const ly = gy * cell
          const wx = Math.round(cx0 + lx * cosA - ly * sinA)
          const wy = Math.round(cy0 + lx * sinA + ly * cosA)
          const s = sample(wx, wy)
          if (!s) continue
          const [op, l] = s
          // Darkness is the tone: dark logo areas -> big dots, light -> small.
          const tone = Math.pow(1 - l, 0.85)
          const coverage = op * tone

          // Ripple travelling outward from centre.
          const dist = Math.hypot(wx - cx0, wy - cy0)
          const wave = Math.sin(dist * rippleFreq - rippleSpeed)
          const wave01 = 0.5 + 0.5 * wave
          // A little extra swell right on the crest for a crisp ring.
          const crest = Math.pow(wave01, 3)

          const radius =
            Math.sqrt(coverage) * maxR * (0.72 + 0.22 * wave01 + crest * 0.1)
          if (radius < 0.35) continue

          // Brighten dots on the crest so a luminous ring sweeps through.
          const lift = crest * 0.55 * (dist < maxDist ? 1 : 0)
          const r = ink.r + (255 - ink.r) * lift
          const g = ink.g + (255 - ink.g) * lift
          const b = ink.b + (255 - ink.b) * lift
          ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`
          ctx.beginPath()
          ctx.arc(wx, wy, radius, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    },
  },
]
